/** @format */

// File: src/components/relays/RosterOrderConfigModal.jsx
// Description: Roster running order configuration modal supporting selective eligibility mapping and duplication blocks.
// Version: 3.2.3

import { useState, useEffect } from "@wordpress/element";
import Swal from "sweetalert2";

const RosterOrderConfigModal = ({
    teamId,
    team,
    activeEvent,
    clubRoster,
    isDeadlinePassed,
    onClose,
    setDeclaredTeams
}) => {
    // Array size extended to 8 to support 4 primary legs and 4 alternate slots
    const [legs, setLegs] = useState([null, null, null, null, null, null, null, null]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if(team) {
            const filledLegs = [null, null, null, null, null, null, null, null];
            if(Array.isArray(team.runners)) {
                for(let i = 0; i < 8; i++) {
                    filledLegs[i] = team.runners[i] ? Number(team.runners[i]) : null;
                }
            }
            setLegs(filledLegs);
        }
    }, [team]);

    // Normalize utility string inputs to guarantee accurate mapping criteria
    const strictNormalize = (str) => (str ? String(str).trim().toLowerCase() : "");

    const normalizeGender = (genderStr) => {
        const clean = strictNormalize(genderStr);
        if(["m", "male", "men", "boy", "boys"].includes(clean)) return "m";
        if(["f", "female", "women", "lady", "ladies", "girl", "girls"].includes(clean)) return "f";
        if(["mixed", "open", "x"].includes(clean)) return "mixed";
        return clean;
    };

    // Evaluate primary rule matrix targeting the Event's strict sexCode specifications
    const targetGenderRaw = activeEvent?.sexCode || team?.sexCode || team?.gender || "";
    const targetGenderNormalized = normalizeGender(targetGenderRaw);

    // Filter roster athletes strictly matching the Relay Category's gender/sex rule metric
    const eligiblePool = clubRoster.filter((athlete) => {
        if(!targetGenderNormalized || targetGenderNormalized === "mixed") {
            return true; // Bypass mapping block for true Open/Mixed structural divisions
        }

        const athleteGenderNormalized = normalizeGender(athlete.sexCode || athlete.gender || "");
        return athleteGenderNormalized === targetGenderNormalized;
    });

    const handleLegChange = (legIndex, value) => {
        const updatedLegs = [...legs];
        updatedLegs[legIndex] = value ? Number(value) : null;
        setLegs(updatedLegs);
    };

    const handleSaveRosterConfig = (e) => {
        e.preventDefault();
        if(typeof isDeadlinePassed === "function" ? isDeadlinePassed() : !!isDeadlinePassed) return;

        setSaving(true);
        const globalData = window.ammData;

        fetch(`${ globalData.api_url }/relay-teams/${ teamId }/legs`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": globalData.nonce
            },
            body: JSON.stringify({ legs: legs })
        })
            .then((res) => {
                if(!res.ok) throw new Error("Network configuration mapping mutation rejection.");
                return res.json();
            })
            .then((data) => {
                if(data.success) {
                    setDeclaredTeams((prev) =>
                        prev.map((t) => (t.id === teamId ? { ...t, runners: legs } : t))
                    );
                    Swal.fire({
                        title: "Sequence Saved",
                        text: "Running order configurations have been committed safely.",
                        icon: "success",
                        timer: 1500,
                        showConfirmButton: false
                    });
                    onClose();
                } else {
                    throw new Error(data.message || "Failed saving roster order indices.");
                }
            })
            .catch((err) => {
                Swal.fire({
                    title: "Mutation Error",
                    text: err.message || "An unresolved exception occurred writing data schemas.",
                    icon: "error"
                });
            })
            .finally(() => {
                setSaving(false);
            });
    };

    const deadlineActive = typeof isDeadlinePassed === "function" ? isDeadlinePassed() : !!isDeadlinePassed;

    return (
        <div className="amm-modal-overlay d-flex align-items-center justify-content-center" style={ { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 999999 } }>
            <div className="card shadow-lg border-0 amm-modal-content animate__animated animate__fadeInUp" style={ { width: "100%", maxWidth: "560px" } }>
                <div className="card-header bg-dark text-white p-3 d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="fw-bold mb-0 text-white text-uppercase tracking-wide" style={ { fontSize: "14px" } }>
                            Configure Running Order & Pool
                        </h5>
                        <small className="text-light text-uppercase opacity-75" style={ { fontSize: "11px" } }>
                            Target Class: { activeEvent?.disciplineName || "Relay Squad" } ({ targetGenderNormalized.toUpperCase() })
                        </small>
                    </div>
                    <button type="button" className="btn-close btn-close-white shadow-none" onClick={ onClose } aria-label="Close" disabled={ saving }></button>
                </div>
                <div className="card-body bg-light p-4" style={ { maxHeight: "70vh", overflowY: "auto" } }>

                    {/* Primary Running Order Block */ }
                    <h6 className="fw-bold text-secondary text-uppercase small tracking-wider mb-2" style={ { fontSize: "11px" } }>Primary Roster Slots</h6>
                    <div className="d-flex flex-column gap-2 mb-4">
                        { [0, 1, 2, 3].map((legIdx) => {
                            const currentLegSelection = legs[legIdx];
                            return (
                                <div className="row align-items-center bg-white p-2 rounded border border-light-subtle mx-0 shadow-sm" key={ `leg-config-row-${ legIdx }` }>
                                    <div className="col-3 text-start d-flex align-items-center">
                                        <span className="badge bg-secondary-subtle text-secondary-emphasis border fw-bold text-uppercase px-2 py-1" style={ { fontSize: "11px" } }>
                                            Leg { legIdx + 1 }
                                        </span>
                                    </div>
                                    <div className="col-9">
                                        <select
                                            className="form-select form-select-sm border-light-subtle text-dark fw-medium rounded shadow-none"
                                            value={ currentLegSelection || "" }
                                            onChange={ (e) => handleLegChange(legIdx, e.target.value) }
                                            disabled={ deadlineActive || saving }
                                            style={ { fontSize: "13px" } }
                                        >
                                            <option value="">-- Assign Athlete Roster Index --</option>
                                            { eligiblePool.map((ath) => {
                                                const assignedElsewhere = legs.some((l, idx) => idx !== legIdx && Number(l) === Number(ath.id));
                                                return (
                                                    <option
                                                        key={ `ath-opt-leg-${ legIdx }-${ ath.id }` }
                                                        value={ ath.id }
                                                        disabled={ assignedElsewhere }
                                                    >
                                                        { ath.lastName.toUpperCase() }, { ath.firstName } { assignedElsewhere ? "(Already Chosen)" : "" }
                                                    </option>
                                                );
                                            }) }
                                        </select>
                                    </div>
                                </div>
                            );
                        }) }
                    </div>

                    {/* Alternates Pool Block */ }
                    <h6 className="fw-bold text-warning text-uppercase small tracking-wider mb-2" style={ { fontSize: "11px" } }>Relay Alternates Pool</h6>
                    <div className="d-flex flex-column gap-2">
                        { [4, 5, 6, 7].map((legIdx) => {
                            const currentLegSelection = legs[legIdx];
                            return (
                                <div className="row align-items-center bg-white p-2 rounded border border-warning-subtle mx-0 shadow-sm" key={ `leg-config-row-${ legIdx }` }>
                                    <div className="col-3 text-start d-flex align-items-center">
                                        <span className="badge bg-warning-subtle text-warning-emphasis border fw-bold text-uppercase px-2 py-1" style={ { fontSize: "11px" } }>
                                            Alt { legIdx - 3 }
                                        </span>
                                    </div>
                                    <div className="col-9">
                                        <select
                                            className="form-select form-select-sm border-light-subtle text-dark fw-medium rounded shadow-none"
                                            value={ currentLegSelection || "" }
                                            onChange={ (e) => handleLegChange(legIdx, e.target.value) }
                                            disabled={ deadlineActive || saving }
                                            style={ { fontSize: "13px" } }
                                        >
                                            <option value="">-- Vacant Slot --</option>
                                            { eligiblePool.map((ath) => {
                                                const assignedElsewhere = legs.some((l, idx) => idx !== legIdx && Number(l) === Number(ath.id));
                                                return (
                                                    <option
                                                        key={ `ath-opt-leg-${ legIdx }-${ ath.id }` }
                                                        value={ ath.id }
                                                        disabled={ assignedElsewhere }
                                                    >
                                                        { ath.lastName.toUpperCase() }, { ath.firstName } { assignedElsewhere ? "(Already Chosen)" : "" }
                                                    </option>
                                                );
                                            }) }
                                        </select>
                                    </div>
                                </div>
                            );
                        }) }
                    </div>

                </div>
                <div className="card-footer bg-white p-3 border-top d-flex justify-content-end gap-2">
                    <button
                        className="btn btn-secondary btn-sm px-3 fw-semibold"
                        onClick={ (e) => {
                            e.preventDefault();
                            onClose();
                        } }
                        disabled={ saving }
                    >
                        Cancel
                    </button>
                    { !deadlineActive && (
                        <button
                            className="btn btn-primary btn-sm px-4 fw-semibold shadow-sm"
                            onClick={ handleSaveRosterConfig }
                            disabled={ saving }
                        >
                            { saving ? "Saving Sequence..." : "Save Order" }
                        </button>
                    ) }
                </div>
            </div>
        </div>
    );
};

export default RosterOrderConfigModal;